import { Formik, Form } from 'formik';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import useGallery from './hooks/useGallery';
import useTemplates, {
    EmailTemplate,
} from '../../EmailTemplates/components/hooks/useTemplates';
import useSurveyQuestions from './hooks/useSurveyQuestions';
import { Button } from '@/src/shadComponents/ui/button';
import usePicTime from '@/src/Orgs/components/hooks/usePicTime';
import WebGallerySettings from './WebGallerySettings';
import SharingSettings from './SharingSettings';
import DataCollectionSettings from './DataCollectionSettings';
import CrowdGallerySettings from './CrowdGallerySettings';
import FTPSettings from './FTPSettings';
import { cn } from '@/src/lib/utils';
import SmallLoader from '@/src/Common/components/SmallLoader';

interface SurveyQuestion {
    question: string;
    answerTypeId: number;
    id?: number;
    isDeleted?: boolean;
}

interface GalleryMediaFilter {
    enable: boolean;
    original: boolean;
    videos: boolean;
    photos: boolean;
    prints: boolean;
    edited: boolean;
}

interface GalleryObject {
    name: string;
    isPrivate: boolean;
    password: string;
    slug: string;
    id?: number;
    textSettingsId?: number;
    emailSettingsId?: number;
    activationId: number;
    emailBody: string;
    emailEnabled: boolean;
    fromAddress: string;
    fromName: string;
    subject: string;
    textBody: string;
    textEnabled: boolean;
    foregroundColor: string;
    backgroundColor: string;
    qrLogo: string;
    bottomLogo: string;
    logo: string;
    font: string;
    ctaImage: string;
    ctaText: string;
    ctaLink: string;
    headerBackgroundColor: string;
    headerForegroundColor: string;
    sendLinkToPhoto: boolean;
    showGalleryLink: boolean;
    surveyRequired: boolean;
    ageVerification: boolean;
    verifiedAge: number | null;
    surveyQuestions: [SurveyQuestion];
    galleryMediaFilter: GalleryMediaFilter;
    disclaimer: string;
    disclaimerRequired: boolean;
    disclaimerEnabled: boolean;
    disclaimerOptOut: boolean;
    metaPixelId: string;
    syncWithPictime?: boolean;
    createShareDescriptionText: string;
    createShareDescriptionTextSize: number;
    createShareDescriptionTextFont: string;
    surveyUrl: string | null;
    labelSearchPrompt: string | null;
    preShareSurveyUrl: string | null;
    sendAttachments: boolean;
}

type TabType =
    | 'web-gallery'
    | 'sharing'
    | 'data-collection'
    | 'crowd-gallery'
    | 'ftp';

export default function EditGalleryV2() {
    const navigate = useNavigate();
    const [hasPictimeSettings, setHasPictimeSettings] =
        useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);
    const [gallery, setGallery] = useState<GalleryObject | null>(null);
    const { galleryId: id, id: activationId } = useParams();
    const { getGallery, saveGallery } = useGallery();
    const { getSurveyQuestions } = useSurveyQuestions();
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] =
        useState<EmailTemplate | null>(null);
    const { getTemplates } = useTemplates();
    const galleryId = id ? +id : 0;
    const { isLinkedToPictime } = usePicTime();
    const [activeTab, setActiveTab] = useState<TabType>('web-gallery');

    useEffect(() => {
        let isMounted = true;
        const getAccounts = async () => {
            try {
                const gallery = await getGallery(galleryId);
                const surveyQuestions = await getSurveyQuestions(
                    gallery.activationId,
                );
                const templates = await getTemplates();
                const hasPictimeSettings = await isLinkedToPictime();

                gallery.surveyQuestions = surveyQuestions;
                setGallery(gallery);
                setTemplates(templates);
                setHasPictimeSettings(hasPictimeSettings);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        getAccounts();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <SmallLoader />
            </div>
        );
    }

    if (!gallery) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg text-red-600">
                    Gallery loading error, does it exist?
                </div>
            </div>
        );
    }

    const tabs = [
        {
            id: 'web-gallery' as TabType,
            name: 'Web Gallery',
            description: 'Visual appearance and gallery behavior',
        },
        {
            id: 'sharing' as TabType,
            name: 'Sharing',
            description: 'Text and email sharing experience',
        },
        {
            id: 'data-collection' as TabType,
            name: 'Data Collection',
            description: 'Surveys, disclaimers, and data settings',
        },
        {
            id: 'crowd-gallery' as TabType,
            name: 'Crowd Gallery',
            description: 'SMS-based photo collection',
        },
        {
            id: 'ftp' as TabType,
            name: 'FTP Sync',
            description: 'FTP server synchronization',
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Edit Gallery
                    </h1>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/activations/${activationId}`)}
                    >
                        ← Back to Activation Dashboard
                    </Button>
                </div>
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={cn(
                                    'py-2 px-1 border-b-2 font-bold text-sm',
                                    activeTab === tab.id
                                        ? 'border-black text-black dark:text-white'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
                                )}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-6 py-8">
                        {activeTab === 'web-gallery' && (
                            <Formik
                                initialValues={gallery}
                                onSubmit={async (values, actions) => {
                                    const wasSuccessful = await saveGallery(
                                        galleryId,
                                        values,
                                    );
                                    actions.setSubmitting(false);

                                    if (wasSuccessful) {
                                        // Update the gallery state with the new values
                                        setGallery({ ...gallery, ...values });
                                        toast.success(
                                            'Gallery settings saved successfully',
                                        );
                                    } else {
                                        toast.error(
                                            'Failed to save gallery settings',
                                        );
                                    }
                                }}
                            >
                                {(props) => (
                                    <Form
                                        className="space-y-6"
                                        placeholder={undefined}
                                        onPointerEnterCapture={undefined}
                                        onPointerLeaveCapture={undefined}
                                    >
                                        <WebGallerySettings
                                            values={props.values}
                                            setFieldValue={props.setFieldValue}
                                            hasPictimeSettings={
                                                hasPictimeSettings
                                            }
                                        />

                                        {/* Action Buttons */}
                                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => navigate(-1)}
                                            >
                                                Done
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={props.isSubmitting}
                                                className="bg-black hover:bg-gray-800 text-white"
                                            >
                                                {props.isSubmitting
                                                    ? 'Saving...'
                                                    : 'Apply Changes'}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        )}

                        {activeTab === 'sharing' && (
                            <Formik
                                initialValues={gallery}
                                onSubmit={async (values, actions) => {
                                    const wasSuccessful = await saveGallery(
                                        galleryId,
                                        values,
                                    );
                                    actions.setSubmitting(false);

                                    if (wasSuccessful) {
                                        // Update the gallery state with the new values
                                        setGallery({ ...gallery, ...values });
                                        toast.success(
                                            'Sharing settings saved successfully',
                                        );
                                    } else {
                                        toast.error(
                                            'Failed to save sharing settings',
                                        );
                                    }
                                }}
                            >
                                {(props) => (
                                    <Form
                                        className="space-y-6"
                                        placeholder={undefined}
                                        onPointerEnterCapture={undefined}
                                        onPointerLeaveCapture={undefined}
                                    >
                                        <SharingSettings
                                            values={props.values}
                                            setFieldValue={props.setFieldValue}
                                            templates={templates}
                                            selectedTemplate={selectedTemplate}
                                            setSelectedTemplate={
                                                setSelectedTemplate
                                            }
                                        />

                                        {/* Action Buttons */}
                                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => {
                                                    navigate(-1);
                                                }}
                                            >
                                                Done
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={props.isSubmitting}
                                                className="bg-black hover:bg-gray-800 text-white"
                                            >
                                                {props.isSubmitting
                                                    ? 'Saving...'
                                                    : 'Apply Changes'}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        )}

                        {activeTab === 'data-collection' && (
                            <Formik
                                initialValues={gallery}
                                onSubmit={async (values, actions) => {
                                    const wasSuccessful = await saveGallery(
                                        galleryId,
                                        values,
                                    );
                                    actions.setSubmitting(false);

                                    if (wasSuccessful) {
                                        // Update the gallery state with the new values
                                        setGallery({ ...gallery, ...values });
                                        toast.success(
                                            'Data collection settings saved successfully',
                                        );
                                    } else {
                                        toast.error(
                                            'Failed to save data collection settings',
                                        );
                                    }
                                }}
                            >
                                {(props) => (
                                    <Form
                                        className="space-y-6"
                                        placeholder={undefined}
                                        onPointerEnterCapture={undefined}
                                        onPointerLeaveCapture={undefined}
                                    >
                                        <DataCollectionSettings
                                            values={props.values}
                                            setFieldValue={props.setFieldValue}
                                        />

                                        {/* Action Buttons */}
                                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => {
                                                    navigate(-1);
                                                }}
                                            >
                                                Done
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={props.isSubmitting}
                                                className="bg-black hover:bg-gray-800 text-white"
                                            >
                                                {props.isSubmitting
                                                    ? 'Saving...'
                                                    : 'Apply Changes'}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        )}

                        {activeTab === 'crowd-gallery' && (
                            <CrowdGallerySettings galleryId={galleryId} />
                        )}

                        {activeTab === 'ftp' && (
                            <FTPSettings galleryId={galleryId} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
